import { useEffect, useState } from 'react'
import { api, type DictionaryKind, type PendingEntry } from '../api'
import { StarterProfiles } from '../components/StarterProfiles'

/**
 * The market-definition surface — TECHNICAL_DESIGN.md §5.3.3.
 *
 * With no pack to import and no holdings feed until release 2, this is the
 * *only* route by which vocabulary enters the system in the first cut.
 *
 * **The forms are generated from `/api/dictionary/kinds`.** §5.3's claim is that
 * market vocabulary is data and not schema; a hand-written form per type would
 * make that false at the layer the customer actually meets. Adding a dictionary
 * type is a change in the store, not a new form shipped to every client.
 *
 * **Near-duplicates must be named before an entry is approved.** `duration`
 * against `duration_years` scores 0.6, and once one rule cites each the two are
 * not reconcilable while every screen still looks correct. The approve button
 * therefore submits the codes the approver was *shown*, and the store refuses a
 * set that has moved since — so the acknowledgement records a decision rather
 * than a field somebody defaulted. Where there are neighbours, the button says
 * so rather than reading "Approve".
 */
export function Dictionary({
  canEdit,
  canSeed,
  section,
}: {
  canEdit: boolean
  canSeed: boolean
  /** Chosen from the main menu — see `App`. */
  section: 'terms' | 'requests' | 'starter'
}) {
  const [kinds, setKinds] = useState<DictionaryKind[]>([])
  const [kind, setKind] = useState<string>('measure')
  const [entries, setEntries] = useState<Record<string, string>[]>([])
  const [queue, setQueue] = useState<PendingEntry[]>([])
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const spec = kinds.find((k) => k.name === kind)

  useEffect(() => {
    api
      .dictionaryKinds()
      .then(setKinds)
      .catch((e) => setError(String(e.message ?? e)))
  }, [])

  async function load() {
    setError(null)
    try {
      setEntries(await api.dictionary(kind))
      setQueue(await api.vocabularyQueue())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
  }, [kind])

  async function ask(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    setNotice(null)
    try {
      const result = await api.requestEntry(kind, draft)
      setDraft({})
      setNotice(
        result.near_duplicates.length
          ? `Requested. Close to existing vocabulary: ${result.near_duplicates
              .map((d) => `${d.code} (${d.score.toFixed(2)})`)
              .join(', ')}. An approver will have to confirm this is distinct.`
          : 'Requested. An approver decides whether it enters the language.',
      )
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function decide(entry: PendingEntry, action: 'approve' | 'reject') {
    setError(null)
    try {
      if (action === 'approve') {
        await api.approveEntry(
          entry.kind,
          entry.entry_id,
          entry.near_duplicates.map((d) => d.code),
        )
      } else {
        const reason = window.prompt('Why is this being declined?')
        if (!reason?.trim()) return
        await api.rejectEntry(entry.kind, entry.entry_id, reason)
      }
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="dictionary">

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {notice && <p className="ok">{notice}</p>}

      {section === 'starter' && <StarterProfiles canSeed={canSeed} />}

      {section === 'requests' && (
        <section>
          <header className="page-head">
            <h2>Requests</h2>
            <p className="note">
              An author who needs a term the Dictionary does not have asks for it here.
              Requesting does not define it: an approver decides whether it enters
              the language, because adding a measure changes what limits this
              appliance can express.
            </p>
          </header>

          {spec && (
            <form className="card guided" onSubmit={ask} data-testid="guided-form">
              <h3>Request a {spec.label}</h3>
              <div className="tabstrip sub" role="tablist" aria-label="Dictionary type">
                {kinds.map((k) => (
                  <button
                    type="button"
                    key={k.name}
                    role="tab"
                    aria-selected={kind === k.name}
                    onClick={() => setKind(k.name)}
                  >
                    {k.label}
                  </button>
                ))}
              </div>
              {/* Generated from the metadata, never hand-written per type. */}
              <div className="row-form">
                {spec.fields.map((field) => (
                  <label key={field}>
                    {field}
                    {spec.required.includes(field) && <span aria-hidden> *</span>}
                    <input
                      value={draft[field] ?? ''}
                      required={spec.required.includes(field)}
                      onChange={(e) => setDraft({ ...draft, [field]: e.target.value })}
                    />
                  </label>
                ))}
                <button type="submit">Request</button>
              </div>
            </form>
          )}

          <section className="queue" data-testid="vocab-queue">
            <h3>Awaiting an approver</h3>
            {queue.length === 0 ? (
              <p className="empty">Nothing is waiting.</p>
            ) : (
              <ul>
                {queue.map((entry) => (
                  <li key={entry.entry_id} data-testid="vocab-request">
                    <code>{entry.values.code ?? JSON.stringify(entry.values)}</code>
                    <span className="layer">{entry.label}</span>
                    <span className="source">{entry.source}</span>
                    {entry.near_duplicates.length > 0 && (
                      <span className="warn" data-testid="near-duplicate">
                        close to{' '}
                        {entry.near_duplicates
                          .map((d) => `${d.code} (${d.score.toFixed(2)})`)
                          .join(', ')}
                      </span>
                    )}
                    {canEdit && (
                      <>
                        <button onClick={() => void decide(entry, 'approve')}>
                          {entry.near_duplicates.length
                            ? 'Approve — distinct from the above'
                            : 'Approve'}
                        </button>
                        <button className="ghost" onClick={() => void decide(entry, 'reject')}>
                          Decline
                        </button>
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </section>
      )}

      {section === 'terms' && (
        <section className="reference">
          <header className="page-head">
            <h2>Terms rules can cite</h2>
            <p className="note">
              The vocabulary this appliance understands today. A rule may only be
              written in these terms — which is what stops two desks meaning two
              different things by the same word.
            </p>
          </header>

          <div className="tabstrip sub" role="tablist" aria-label="Dictionary type">
            {kinds.map((k) => (
              <button
                type="button"
                key={k.name}
                role="tab"
                aria-selected={kind === k.name}
                onClick={() => setKind(k.name)}
              >
                {k.label}
              </button>
            ))}
          </div>

          {entries.length === 0 ? (
            <p className="empty">
              Nothing defined yet for this type. Starter markets will fill it in.
            </p>
          ) : (
            <table className="grid">
              <thead>
                <tr>
                  <th>Term</th>
                  <th>Meaning</th>
                  <th>Unit</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={String(entry.id)} data-testid="entry">
                    <td>
                      <code>{entry.code ?? `${entry.agency} ${entry.notch}`}</code>
                      {entry.canonical_notch && (
                        <span className="clause"> → {entry.canonical_notch}</span>
                      )}
                    </td>
                    <td>{entry.description ?? <span className="empty">—</span>}</td>
                    <td className="clause">{entry.unit ?? ''}</td>
                    <td>
                      <span className="pill">{entry.source}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  )
}
